import type {Match} from '@wandb/weave/common/types/base';
import {getCookie} from '@wandb/weave/common/util/cookie';
import React, {useEffect, useLayoutEffect, useMemo} from 'react';
import {useLocation} from 'react-router';
import {v4 as uuidv4} from 'uuid';

import {useTrackRouteOrgAccountTypeQuery} from '../generated/graphql';
import {
  getAccountPlanTypeFromPrimarySub,
  getPrimarySub,
} from '../util/accounts/pricing';
import {
  endPageMetricSession,
  initPageMetricSession,
} from '../util/datadog/artifacts/pageMetricSession';
import {
  setRumViewContextProperty,
  startDatadogRumView,
} from '../util/datadog/datadogRum';
import {trackPage} from '../util/navigation';
import type {AppRouteData} from './types';
import {useCurrentRouteMatchParams} from './utils';

const HOST_SESSION_ID_COOKIE = `host_session_id`;

export const getRouteTab = (
  match: Match,
  additionalProps: AppRouteData['additionalProps']
) => {
  if (match?.params?.tab) {
    return match.params.tab;
  }
  if ('tab' in additionalProps) {
    return additionalProps.tab;
  }
  return undefined;
};

/**
 * A lightweight wrapper component that handles analytics and datadog RUM tracking at route level.
 * This should only wrap the component that gets rendered when the path matches because we only want
 * to trigger tracking ONCE.
 */
export const TrackRouteComponent = ({
  children,
  datadogViewName,
  tabParam,
}: {
  children: React.ReactNode;
  datadogViewName: string;
  /** @deprecated to be removed in favor of just `datadogViewName` (with envIsDev validation) */
  tabParam?: string;
}) => {
  return (
    <>
      {/* Keep this as the first sibling: some vitals/timings start from child
      layout effects. Parent layout effects run after child effects, but sibling
      effects run in render order, so the new RUM view exists first. */}
      <DatadogRumViewStart
        datadogViewName={datadogViewName}
        tabParam={tabParam}
      />
      {children}
    </>
  );
};

function DatadogRumViewStart({
  datadogViewName,
  tabParam,
}: {
  datadogViewName: string;
  tabParam?: string;
}) {
  const tabSuffix = tabParam ? `:${tabParam}` : '';
  const datadogViewNameWithTab = datadogViewName + tabSuffix;

  const location = useLocation();
  const routeChangeKey = `${location.pathname}${location.search}${location.hash}`;

  const routePerfContext = useMemo(
    () => ({
      routeChangeKey,
      routeTrackingId: uuidv4(),
      routeTrackingPerfStart: Date.now(),
    }),
    [routeChangeKey]
  );

  useLayoutEffect(() => {
    startDatadogRumView(datadogViewNameWithTab);
    // RUM views restart on route changes. A label-only change should not start a new view.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChangeKey]);

  // entity/project come from the route synchronously; org/account type are resolved
  // through a non-blocking Apollo query and applied to the active RUM view when ready.
  const {entityName, projectName} = useCurrentRouteMatchParams();

  const {data} = useTrackRouteOrgAccountTypeQuery({
    variables: {entityName: entityName ?? ''},
    fetchPolicy: 'cache-first',
    skip: !entityName,
  });
  const org = data?.entity?.organization;
  const orgName = org?.name;
  const orgAccountType =
    org == null
      ? undefined
      : getAccountPlanTypeFromPrimarySub(getPrimarySub(org));

  useEffect(() => {
    // `routeName`/`tab` are external analytics-schema keys consumed by segment/
    // mixpanel/perf events, so keep the key names and pass the view name as the value.
    initPageMetricSession(
      datadogViewNameWithTab,
      {routeName: datadogViewName, tab: tabParam},
      {
        uid: routePerfContext.routeTrackingId,
        startTime: routePerfContext.routeTrackingPerfStart,
      }
    );

    trackPage(
      {
        routeName: datadogViewName,
        tab: tabParam,
        routeAndTab: datadogViewNameWithTab,
        isNew: true,
        routeTrackingId: routePerfContext.routeTrackingId,
        routeTrackingPerfStart: routePerfContext.routeTrackingPerfStart,
      },
      {
        context: {
          hostSessionID: getCookie(HOST_SESSION_ID_COOKIE),
        },
      }
    );

    // Do not reset RUM view state (timings, backgrounded flag) here:
    // this cleanup runs while the old SDK view is still current, which lets
    // still-mounted timing hooks refire against it. startDatadogRumView resets
    // that state with the next view.
    return () => {
      endPageMetricSession(routePerfContext.routeTrackingId);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChangeKey]);

  useEffect(() => {
    setRumViewContextProperty('identity.entity', entityName);
    setRumViewContextProperty('identity.project', projectName);
    setRumViewContextProperty('identity.org', orgName);
    setRumViewContextProperty('identity.org_account_type', orgAccountType);

    // For backwards compatibility
    setRumViewContextProperty('entityName', entityName);
    setRumViewContextProperty('projectName', projectName);
  }, [entityName, projectName, orgName, orgAccountType, routeChangeKey]);

  return null;
}
